import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogContent, Box, Typography, Button, Divider, Tooltip } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { VotemateLogo } from '../votemate-logo/VotemateLogo';
import { semanticColors, BORDER_RADIUS } from '@/theme/tokens';
import { ExternalLink } from '../ui-library/ExternalLink';
import { EXTERNAL_URLS } from '@/constants/urls';

const PROVIDER_ENABLED = {
  facebook: true,
  microsoft: true,
  apple: true,
} as const;

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onGoogleLogin: () => void;
  onFacebookLogin: () => void;
  onMicrosoftLogin: () => void;
  onAppleLogin: () => void;
  onEmailLogin: () => void;
  // Staging-only password login entry. Rendered only when VITE_DEBUG is
  // 'true' (staging and local dev) so automated browser tools (Playwright,
  // Demosmith) can bypass the production passwordless OTP flow.
  onPasswordLogin: () => void;
}

const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);

const FacebookIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      fill="#1877F2"
    />
  </svg>
);

const MicrosoftIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="0" y="0" width="10" height="10" fill="#f25022" />
    <rect x="11" y="0" width="10" height="10" fill="#7fba00" />
    <rect x="0" y="11" width="10" height="10" fill="#00a4ef" />
    <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
  </svg>
);

const AppleIcon: React.FC = () => (
  <svg
    width="17"
    height="20"
    viewBox="0 0 17 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M14.045 10.374c-.02-2.184 1.788-3.243 1.868-3.295-1.018-1.49-2.6-1.693-3.163-1.713-1.343-.136-2.626.793-3.307.793-.68 0-1.724-.776-2.84-.754-1.456.022-2.804.847-3.553 2.146-1.52 2.634-.388 6.527 1.089 8.663.726 1.045 1.587 2.215 2.718 2.172 1.096-.044 1.508-.703 2.833-.703 1.324 0 1.7.703 2.857.679 1.174-.022 1.917-1.065 2.633-2.117a10.8 10.8 0 0 0 1.2-2.436c-.028-.013-2.308-.886-2.33-3.235zM11.876 3.73c.6-.737 1.008-1.756.896-2.777-.866.036-1.924.583-2.547 1.307-.554.643-1.044 1.68-.862 2.672.964.074 1.95-.49 2.513-1.202z" />
  </svg>
);

const loginButtonSx = {
  textTransform: 'none' as const,
  borderRadius: BORDER_RADIUS,
  py: 1,
  px: 2.5,
  fontSize: '14px',
  fontWeight: 500,
  border: `1px solid ${semanticColors.borderLight}`,
  color: semanticColors.textBody,
  backgroundColor: semanticColors.surface,
  justifyContent: 'center',
  gap: 1,
  minHeight: '44px',
  whiteSpace: 'nowrap' as const,
  '& .MuiButton-startIcon': {
    flexShrink: 0,
  },
  '&:hover': {
    backgroundColor: semanticColors.surfaceSubtle,
    borderColor: semanticColors.borderMedium,
  },
};

// Email button: tighter 16px horizontal padding with centered text that wraps
// instead of overflowing on longer labels (e.g. Spanish "Continuar con correo
// electrónico").
const emailButtonSx = {
  ...loginButtonSx,
  px: 2,
  whiteSpace: 'normal' as const,
  textAlign: 'center' as const,
};

const iconOnlyButtonSx = {
  ...loginButtonSx,
  px: 1,
  '&.Mui-disabled': {
    opacity: 0.4,
    borderColor: semanticColors.borderLight,
  },
};

const secondaryProviders = [
  { key: 'facebook' as const, icon: <FacebookIcon />, label: 'Facebook' },
  { key: 'microsoft' as const, icon: <MicrosoftIcon />, label: 'Microsoft' },
  { key: 'apple' as const, icon: <AppleIcon />, label: 'Apple' },
];

const LoginModal: React.FC<LoginModalProps> = ({
  open,
  onClose,
  onGoogleLogin,
  onFacebookLogin,
  onMicrosoftLogin,
  onAppleLogin,
  onEmailLogin,
  onPasswordLogin,
}) => {
  const { t } = useTranslation('auth');
  const showPasswordLogin = import.meta.env.VITE_DEBUG === 'true';
  const secondaryHandlers = {
    facebook: onFacebookLogin,
    microsoft: onMicrosoftLogin,
    apple: onAppleLogin,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="login-modal-title"
      sx={{
        // Raised above the app chrome (hamburger 1600, LeftPaneMenu 1650-1670,
        // dashboard strips 1660-1700, FeedbackSurveyChip 1900) so the backdrop
        // dimming covers everything behind the modal. MUI's default
        // theme.zIndex.modal (1300) is below those, which left the chrome
        // poking through the tint.
        zIndex: 2000,
        '& .MuiDialog-paper': {
          borderRadius: BORDER_RADIUS,
          px: 0,
          py: 0.5,
          backgroundColor: semanticColors.surface,
          maxWidth: '380px',
          m: { xs: 1.5, sm: 4 },
          width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          pt: { xs: 2.5, sm: 3 },
          pb: { xs: 2, sm: 2.5 },
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <VotemateLogo size={32} logoOnly />

          <Typography
            id="login-modal-title"
            variant="h6"
            sx={{
              mt: { xs: 1.5, sm: 2 },
              mb: 0.75,
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {t('title')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: { xs: 2, sm: 3 },
              textAlign: 'center',
              color: semanticColors.textTertiary,
              lineHeight: 1.5,
              fontSize: '13px',
            }}
          >
            {t('subtitle')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, width: '100%' }}>
            {/* Google — full width, active */}
            <Button
              variant="outlined"
              fullWidth
              onClick={onGoogleLogin}
              startIcon={<GoogleIcon />}
              sx={loginButtonSx}
            >
              {t('continueWithGoogle')}
            </Button>

            {/* Facebook / Microsoft / Apple — icon-only row, disabled until keys configured */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {secondaryProviders.map(({ key, icon, label }) => (
                <Tooltip key={key} title={PROVIDER_ENABLED[key] ? '' : t('comingSoon')} arrow>
                  <Box sx={{ flex: 1 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={secondaryHandlers[key]}
                      disabled={!PROVIDER_ENABLED[key]}
                      aria-label={t('continueWithProvider', { provider: label })}
                      sx={iconOnlyButtonSx}
                    >
                      {icon}
                    </Button>
                  </Box>
                </Tooltip>
              ))}
            </Box>

            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 0.25 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography
                variant="caption"
                sx={{ color: semanticColors.textTertiary, fontSize: '12px' }}
              >
                {t('or')}
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Email — full width */}
            <Button
              variant="outlined"
              fullWidth
              onClick={onEmailLogin}
              startIcon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={emailButtonSx}
            >
              {t('continueWithEmail')}
            </Button>

            {showPasswordLogin && (
              <Button
                variant="text"
                fullWidth
                onClick={onPasswordLogin}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 400,
                  minHeight: '44px',
                  color: semanticColors.textTertiary,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                  '&.Mui-focusVisible': {
                    outline: `2px solid ${semanticColors.borderMedium}`,
                    outlineOffset: '2px',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {t('continueWithPassword')}
              </Button>
            )}

            {/* Continue without signing in — text only */}
            <Button
              variant="text"
              fullWidth
              onClick={onClose}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 400,
                color: semanticColors.textTertiary,
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                  color: semanticColors.textTertiary,
                },
              }}
            >
              {t('continueWithoutSigningIn')}
            </Button>
          </Box>

          <Typography
            variant="caption"
            sx={{
              mt: { xs: 1, sm: 1.5 },
              textAlign: 'center',
              color: semanticColors.textCaption,
              lineHeight: 1.4,
              fontSize: '11px',
            }}
          >
            <Trans
              t={t}
              i18nKey="termsConfirmation"
              components={{
                termsLink: (
                  <ExternalLink href={EXTERNAL_URLS.TERMS_OF_USE} style={{ fontSize: '11px' }} />
                ),
                privacyLink: (
                  <ExternalLink href={EXTERNAL_URLS.PRIVACY_POLICY} style={{ fontSize: '11px' }} />
                ),
              }}
            />
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
